import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import styles from './ItemsAdmin.module.css'
import axios from 'axios'
import config from '../../config'

const ItemsAdmin: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const [items, setItems] = useState<any[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [newItemName, setNewItemName] = useState('')
  const [newItemType, setNewItemType] = useState<'food' | 'drink'>('food')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchType, setSearchType] = useState<'all' | 'food' | 'drink'>('all')

  const isAdmin = user?.email === 'jflorence0@gmail.com'

  useEffect(() => {
    if (isAdmin) {
      fetchItems()
    }
  }, [isAdmin, searchQuery, searchType])

  const handleSearch = () => {
    fetchItems()
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchType('all')
  }

  const fetchItems = async () => {
    if (!isAdmin) return
    try {
      setItemsLoading(true)
      setError(null)
      const params = new URLSearchParams()
      if (searchQuery.trim()) {
        params.append('q', searchQuery.trim())
      }
      if (searchType !== 'all') {
        params.append('type', searchType)
      }
      params.append('limit', '100')
      
      const { data } = await axios.get(`${config.API_BASE_URL}/api/items/?${params.toString()}`)
      setItems(data)
    } catch (error) {
      console.error('Error loading items:', error)
      setError('Failed to load items')
    } finally {
      setItemsLoading(false)
    }
  }

  const createItem = async () => {
    if (!newItemName.trim()) return
    
    try {
      setError(null)
      await axios.post(`${config.API_BASE_URL}/api/items/`, {
        name: newItemName.trim(),
        type: newItemType
      }, {
        params: {
          created_by: 'team',
          approved: true  // All admin-created items are pre-approved
        }
      })
      
      setNewItemName('')
      setNewItemType('food')
      setSuccess('Item created successfully!')
      fetchItems() // Refresh the list
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (error) {
      console.error('Error creating item:', error)
      setError('Failed to create item')
    }
  }

  const updateItemApproval = async (itemId: string, approved: boolean) => {
    try {
      setError(null)
      await axios.put(`${config.API_BASE_URL}/api/items/${itemId}`, {
        approved: approved
      })
      setSuccess(`Item ${approved ? 'approved' : 'unapproved'} successfully!`)
      fetchItems() // Refresh the list
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (error) {
      console.error('Error updating item:', error)
      setError('Failed to update item')
    }
  }

  const deleteItem = async (itemId: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return
    
    try {
      setError(null)
      await axios.delete(`${config.API_BASE_URL}/api/items/${itemId}`)
      setSuccess('Item deleted successfully!')
      fetchItems() // Refresh the list
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (error) {
      console.error('Error deleting item:', error)
      setError('Failed to delete item')
    }
  }

  if (!user) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.brand}>PLATEFULL Items Admin</div>
        </div>
        <div className={styles.content}>
          <p>Please sign in.</p>
        </div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.brand}>PLATEFULL Items Admin</div>
        </div>
        <div className={styles.content}>
          <p>Admin access required.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.brand}>PLATEFULL Items Admin</div>
        <div className={styles.headerActions}>
          <button className={styles.backButton} onClick={() => navigate('/admin')}>
            ← Back to Admin
          </button>
          <button className={styles.logoutButton} onClick={logout}>
            Log out
          </button>
        </div>
      </div>
      
      <div className={styles.content}>
        <div className={styles.pageHeader}>
          <h1>Favorites Items Management</h1>
          <p>Create and manage food and drink items that users can add as favorites</p>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        {success && (
          <div className={styles.successMessage}>
            {success}
          </div>
        )}

        <div className={styles.createSection}>
          <h2>Create New Item</h2>
          <div className={styles.createForm}>
            <div className={styles.formRow}>
              <input
                type="text"
                placeholder="Item name (e.g., Old Fashioned, French Onion Soup)"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                className={styles.nameInput}
                onKeyPress={(e) => e.key === 'Enter' && createItem()}
              />
              <select
                value={newItemType}
                onChange={(e) => setNewItemType(e.target.value as 'food' | 'drink')}
                className={styles.typeSelect}
              >
                <option value="food">Food</option>
                <option value="drink">Drink</option>
              </select>
              <button 
                className={styles.createButton}
                onClick={createItem}
                disabled={!newItemName.trim()}
              >
                Create Item
              </button>
            </div>
          </div>
        </div>

        <div className={styles.searchSection}>
          <h2>Search Items</h2>
          <div className={styles.searchForm}>
            <div className={styles.formRow}>
              <input
                type="text"
                placeholder="Search by name or normalized name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'all' | 'food' | 'drink')}
                className={styles.typeSelect}
              >
                <option value="all">All Types</option>
                <option value="food">Food Only</option>
                <option value="drink">Drink Only</option>
              </select>
              <button 
                className={styles.searchButton}
                onClick={handleSearch}
                disabled={itemsLoading}
              >
                {itemsLoading ? 'Searching...' : 'Search'}
              </button>
              <button 
                className={styles.clearButton}
                onClick={clearSearch}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className={styles.itemsSection}>
          <div className={styles.sectionHeader}>
            <h2>Items ({items.length})</h2>
            <button 
              className={styles.refreshButton}
              onClick={fetchItems}
              disabled={itemsLoading}
            >
              {itemsLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {itemsLoading ? (
            <div className={styles.loadingState}>
              <p>Loading items...</p>
            </div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No items found. Create your first item above!</p>
            </div>
          ) : (
            <div className={styles.itemsTable}>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Normalized Name</th>
                    <th>Type</th>
                    <th>Approved</th>
                    <th>Created By</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className={styles.nameCell}>{item.name}</td>
                      <td className={styles.normalizedCell}>{item.normalized_name}</td>
                      <td>
                        <span className={`${styles.typeBadge} ${item.type === 'food' ? styles.foodBadge : styles.drinkBadge}`}>
                          {item.type}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`${styles.toggleButton} ${item.approved ? styles.approved : styles.unapproved}`}
                          onClick={() => updateItemApproval(item.id, !item.approved)}
                        >
                          {item.approved ? '✓ Approved' : '✗ Unapproved'}
                        </button>
                      </td>
                      <td>{item.created_by || 'Unknown'}</td>
                      <td className={styles.dateCell}>
                        {new Date(item.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <button
                          className={styles.deleteButton}
                          onClick={() => deleteItem(item.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ItemsAdmin
